/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The review page's "Owner rating" panel (design 2026-10-29), in the header strip
 * (`ReviewOwnersStrip`) and the "What owners say" card (`ReviewOwnersCarousel`).
 */
import clsx from 'clsx'
import styles from './ReviewOwnerRating.module.css'
import type { OwnerCardNote } from 'lib/dealhunt/types'
import {
  type CardOwnersView,
  OWNERS_VERDICT_MIN,
  ownersVerdict,
  type OwnersVerdictTone,
} from 'lib/owner-notes/card-owners'

/** "humble_juniper_63" → "HJ". */
export function handleInitials(handle: string): string {
  const words = handle.split(/[^a-z0-9]+/i).filter(w => /^[a-z]/i.test(w))
  return (words.length ? words : [handle])
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

/** What the rating panel reads: no note, no count (a payload with a count and no note is the
 *  zero state); the verdict's tone, else neutral. */
export function ratingOf(owners: CardOwnersView | null): {
  note: OwnerCardNote | null
  count: number
  buyAgain: CardOwnersView['buyAgain']
  verdict: ReturnType<typeof ownersVerdict>
  tone: OwnersVerdictTone | 'neutral'
} {
  const note = owners?.notes[0] ?? null
  const count = note ? owners?.count ?? 0 : 0
  const buyAgain = note && owners?.buyAgain?.total ? owners.buyAgain : null
  const verdict = ownersVerdict(buyAgain)
  return { note, count, buyAgain, verdict, tone: verdict?.tone ?? 'neutral' }
}

export const reviewsOf = (count: number): string => `${count} owner review${count === 1 ? '' : 's'}`

/**
 * The "Owner rating" panel of the header strip, and the row over the "What owners say" card's
 * notes. Under
 * `OWNERS_VERDICT_MIN` buy-again answers: the review count, the split so far, and a bar toward
 * the word; from it, the word, split, bar and percentage in the verdict's tone.
 */
export const OwnerRating: React.VFC<{
  owners: CardOwnersView | null
  /** `row` (the "What owners say" card): one line over a full-width bar, from md. */
  layout?: 'panel' | 'row'
  className?: string
}> = ({ owners, layout = 'panel', className }) => {
  const { note, count, buyAgain, verdict, tone } = ratingOf(owners)
  const reviews = reviewsOf(count)
  const answers = buyAgain?.total ?? 0
  let body = <span className={clsx(styles.rating, styles.plain)}>{'No owner reviews yet'}</span>
  if (verdict && buyAgain) {
    const pct = Math.round((buyAgain.yes / buyAgain.total) * 100)
    body = (
      <>
        <span className={styles.rating}>{verdict.word}</span>
        <span className={styles.count}>
          {`${buyAgain.yes} of ${buyAgain.total} owners would buy again`}
        </span>
        <span className={styles.bar} aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </span>
        <span className={styles.sub}>{`${pct}% positive, from ${reviews}`}</span>
      </>
    )
  } else if (note) {
    body = (
      <>
        <span className={clsx(styles.rating, styles.plain)}>{reviews}</span>
        <span className={styles.count}>
          {buyAgain ? (
            <>
              <span className={styles.split}>{`${buyAgain.yes} of ${buyAgain.total}`}</span>
              {' would buy again'}
            </>
          ) : (
            'Early days for this pair'
          )}
        </span>
        <span className={styles.bar} aria-hidden="true">
          <span style={{ width: `${(answers / OWNERS_VERDICT_MIN) * 100}%` }} />
        </span>
        <span className={styles.sub}>
          {`${OWNERS_VERDICT_MIN - answers} more and we'll show an owner rating`}
        </span>
      </>
    )
  }
  return (
    <div
      className={clsx(styles.verdict, layout === 'row' && styles.row, className)}
      data-tone={tone}
    >
      <span className={styles.eyebrow}>{'Owner rating'}</span>
      {body}
    </div>
  )
}
